// hooks/useUserProfileForm.ts

import { useState, useEffect } from "react";
import { ProfileData } from "../types/profile";

export interface Phone {
  phone: string;
  isPrimary: boolean;
}

export interface Email {
  email: string;
  isPrimary: boolean;
}

export interface UserProfileFormData {
  firstName: string;
  middleName: string;
  lastName: string;
  dob: string;

  emails: Email[];

  addressLine1: string;

  phones: Phone[];
}

export const useUserProfileForm = (profileData: ProfileData | null) => {
  const [formData, setFormData] = useState<UserProfileFormData>({
    firstName: "",
    middleName: "",
    lastName: "",
    dob: "",

    emails: [
      {
        email: "",
        isPrimary: true,
      },
    ],

    addressLine1: "",

    phones: [
      {
        phone: "",
        isPrimary: true,
      },
    ],
  });

  useEffect(() => {
    if (profileData) {
      setFormData({
        firstName: profileData.firstName || "",
        middleName: profileData.middleName || "",
        lastName: profileData.lastName || "",

        dob: profileData.dob ? (typeof profileData.dob === "string" ? profileData.dob.split("T")[0] : profileData.dob.$date.split("T")[0]) : "",

        emails:
          profileData.emails?.map((email) => ({
            email: email.email || "",
            isPrimary: email.isPrimary ?? true,
          })) || [],

        addressLine1: profileData.addressLine1 || "",

        phones:
          profileData.phones?.map((phone) => ({
            phone: phone.phone || "",
            isPrimary: phone.isPrimary ?? true,
          })) || [],
      });
    }
  }, [profileData]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;

    // Handle Email - preserve existing emails
    if (name === "email") {
      setFormData((prev) => ({
        ...prev,
        emails: prev.emails?.length ? prev.emails.map((email, index) => (index === 0 ? { ...email, email: value } : email)) : [{ email: value, isPrimary: true }],
      }));
      return;
    }

    // Handle Phone - preserve existing phones
    if (name === "phone") {
      const numericValue = value.replace(/\D/g, "").slice(0, 10);

      setFormData((prev) => ({
        ...prev,
        phones: prev.phones?.length ? prev.phones.map((phone, index) => (index === 0 ? { ...phone, phone: numericValue } : phone)) : [{ phone: numericValue, isPrimary: true }],
      }));
      return;
    }

    // Handle Normal Fields
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  return {
    formData,
    handleChange,
  };
};
